import {Flex, Stack, Text, Box, Image} from '@chakra-ui/react';
import {IconPlus} from '@tabler/icons';
import React, {useState} from 'react';
import {TextValueRecord} from '../../graphql/datocms-generated';

interface BenefitInfoProps {
  benefitText: string;
}
interface BenefitCardProps {
  benefits: TextValueRecord[];
  quantity: number;
  direction?: 'row' | 'column';
  isTransparent?: boolean;
  hoverAction?: () => void;
  textColor: string;
  coinColor: string;
  imageSrc?: string | undefined;
  iconSrc: string | undefined;
}

const BenefitInfo = ({benefitText}: BenefitInfoProps): JSX.Element => {
  return (
    <Flex alignItems="center">
      <Flex flexShrink={0}>
        <IconPlus size="8px" stroke="4" />
      </Flex>
      <Text textStyle="body4" ml={2} textAlign="left">
        {benefitText}
      </Text>
    </Flex>
  );
};

const BenefitCard: React.FC<BenefitCardProps> = ({
  benefits,
  quantity,
  direction,
  isTransparent,
  hoverAction,
  textColor,
  coinColor,
  imageSrc,
  iconSrc,
}: BenefitCardProps) => {
  const [hover, setHover] = useState<boolean>(false);
  const cardDirection = direction || 'column';

  const benefitsContent = benefits.map((listItem) => {
    return (
      <BenefitInfo
        key={listItem.benefitText}
        benefitText={listItem.benefitText as string}
      />
    );
  });
  return (
    <Stack
      borderRadius="12px"
      bgColor={isTransparent ? 'transparent' : 'white'}
      flex={1}
      p={isTransparent ? 0 : 4}
      spacing={6}
      alignItems="center"
      flexDirection={
        cardDirection === 'row'
          ? cardDirection
          : {base: 'column', md: 'row', lg: 'column'}
      }
      direction={
        cardDirection === 'row' ? cardDirection : {base: 'row', lg: 'column'}
      }
      position="relative"
      cursor={hoverAction ? 'pointer' : 'default'}
      borderWidth="2px"
      borderStyle="solid"
      borderColor="superlight"
      sx={{
        ...(hoverAction && hover
          ? {
              background: '#464750',
              opacity: '0.8',
            }
          : {}),
        ...(hoverAction
          ? {
              '&:focus': {
                borderColor: 'aqua.500',
              },
            }
          : {}),
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={hoverAction ? () => hoverAction() : () => undefined}
      as="button"
    >
      <Flex
        direction="column"
        minWidth={{base: 'inherit', md: '100px'}}
        textStyle="h6"
        textAlign={cardDirection === 'column' ? 'center' : 'right'}
        color="darkGrey"
        alignItems={cardDirection === 'column' ? 'center' : 'flex-end'}
      >
        <Box width="48px" opacity={hoverAction && hover ? '0.1' : 'inherit'}>
          <Image src={iconSrc} width="100%" height="100%" alt="benefit icon" />
        </Box>
        <Flex
          alignItems="center"
          opacity={
            coinColor !== 'bronze' && hoverAction && hover ? '0.5' : 'inherit'
          }
        >
          <Text
            lineHeight="12px"
            color={textColor}
            textStyle="h7"
            ml={1}
            opacity={
              coinColor === 'bronze' && hoverAction && hover ? '0.2' : 'inherit'
            }
          >
            {quantity}
          </Text>
          <Text
            as="span"
            textStyle="body5"
            ml={1}
            color={textColor}
            opacity={
              coinColor === 'bronze' && hoverAction && hover ? '0.4' : 'inherit'
            }
          >
            Pieces
          </Text>
        </Flex>
      </Flex>
      <Stack flexDir="column" spacing={1}>
        {benefitsContent}
      </Stack>

      {imageSrc && (
        <Box
          opacity={hoverAction && hover ? '0.1' : 'inherit'}
          display={{base: 'flex', md: 'block'}}
          minWidth={{base: '165px', md: '200px'}}
        >
          <Image width="200px" src={imageSrc} alt="benefit" />
        </Box>
      )}

      {hoverAction && hover ? (
        <Box
          color="white"
          textAlign="center"
          position="absolute"
          bottom="44%"
          left={{base: '35%', lg: 'inherit'}}
        >
          Select this reward
        </Box>
      ) : null}
    </Stack>
  );
};

export {BenefitCard};
